<template>
  <div class="package-table">
    <div class="smeta-info-banner tw-mb-3">
      <q-icon name="info" color="blue-6" size="16px" class="tw-mr-1" />
      <span>
        Смета последнего подписанного договора
        <template v-if="meta?.version_no != null">
          (версия <b>№{{ meta.version_no }}</b>
          <template v-if="meta?.sign_date"> от {{ formatDate(meta.sign_date) }}</template>)
        </template>.
        Может быть неактуальной, если в смету вносились изменения без подписания нового договора.
      </span>
    </div>

    <div v-if="isSmetaOutdated" class="smeta-outdated-banner tw-mb-3">
      <q-icon name="warning" color="orange-8" size="16px" class="tw-mr-1" />
      <span>
        Сумма сметы договора не совпадает с актуальной сметой подрядчика.
        Необходимо создать и подписать дополнительное соглашение (ДС).
      </span>
    </div>

    <div class="smeta-action-bar tw-flex tw-items-center tw-justify-between tw-mb-4 tw-flex-wrap tw-gap-2">
      <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
        <div class="tw-flex tw-items-center tw-gap-2">
          <q-icon name="history" color="primary" size="20px" />
          <span v-if="meta?.version_no != null" class="smeta-history-label">
            Версия сметы: <b>№{{ meta.version_no }}</b>
            <span v-if="meta?.sign_date" class="tw-ml-2 tw-text-gray-500">
              от {{ formatDate(meta.sign_date) }}
            </span>
          </span>
          <span v-else class="smeta-history-label tw-text-gray-400">
            Нет подписанной версии сметы
          </span>
        </div>
        <div v-if="floorOptions.length" style="min-width: 180px;">
          <q-select
            v-model="selectedFloor"
            :options="floorOptions"
            label="Этаж"
            dense
            outlined
            emit-value
            map-options
            clearable
            options-dense
          />
        </div>
        <q-btn
          v-if="data.length"
          color="green-7"
          icon="file_download"
          label="Excel"
          dense
          unelevated
          no-caps
          :loading="isExporting"
          @click="onExport"
        />
      </div>
      <div
        v-if="!!filteredData?.length"
        class="tw-flex tw-items-center tw-gap-4"
        style="font-weight: 700; font-size: 16px;"
      >
        <div class="tw-flex tw-flex-col tw-items-end">
          <span style="font-size: 11px; color: #888; font-weight: 500;">с НДС</span>
          <span><Amount :value="amount" :round="2" /> тг.</span>
        </div>
        <div style="width: 1px; height: 32px; background: #ddd;" />
        <div class="tw-flex tw-flex-col tw-items-end">
          <span style="font-size: 11px; color: #888; font-weight: 500;">без НДС</span>
          <span><Amount :value="amountWithoutNds" :round="2" /> тг.</span>
        </div>
      </div>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="!isLoading && !filteredData.length" class="tw-text-gray-400 tw-text-sm tw-py-8 tw-text-center">
      Нет данных. Возможно, нет подписанной версии сметы для этого подъезда.
    </div>

    <div v-else style="position: relative;">
      <CustomTable
        :rowsData="filteredData"
        :customHeader="true"
        :custom-body="true"
        :small="true"
        :stickyHeader="true"
        :height="400"
      >
        <template #customHeader>
          <q-tr>
            <q-th align="center" class="th-main">Подъезд<br/>Этаж</q-th>
            <q-th align="left" class="th-main">Работа</q-th>
            <q-th align="right" class="th-smr"><div class="th-group-label">СМР</div>Объем</q-th>
            <q-th align="right" class="th-smr split-header">
              <div class="split-header-top">Цена с НДС</div>
              <div class="split-header-bottom">Цена без НДС</div>
            </q-th>
            <q-th align="right" class="th-smr split-header">
              <div class="split-header-top">Сумма с НДС</div>
              <div class="split-header-bottom">Сумма без НДС</div>
            </q-th>
            <q-th align="left" class="th-mat"><div class="th-group-label">Материал</div>Наименование</q-th>
            <q-th align="right" class="th-mat">Кол-во</q-th>
            <q-th align="right" class="th-mat" style="font-size:11px; color:#888;">Норма</q-th>
            <q-th align="right" class="th-mat split-header">
              <div class="split-header-top">Цена с НДС</div>
              <div class="split-header-bottom">Цена без НДС</div>
            </q-th>
            <q-th align="right" class="th-mat split-header">
              <div class="split-header-top">Сумма с НДС</div>
              <div class="split-header-bottom">Сумма без НДС</div>
            </q-th>
            <q-th align="right" class="th-main split-header">
              <div class="split-header-top">Итого с НДС</div>
              <div class="split-header-bottom">Итого без НДС</div>
            </q-th>
          </q-tr>
        </template>

        <template #customBody="props">
          <q-tr v-if="props.row.isGroupHeader" class="group-header-row">
            <q-td colspan="11" class="group-header-cell">
              {{ props.row.work_set_check_group_name || 'Без группы' }}
            </q-td>
          </q-tr>

          <q-tr v-else>
            <!-- Подъезд / Этаж -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="center"
              vertical-align="center"
            >
              <div class="entrance-cell">
                <span class="entrance-label">П.{{ props.row.entrance }}</span>
                <span class="floor-label">Эт.{{ props.row.level_num }}</span>
              </div>
            </custom-table-cell>

            <!-- Конструктив -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="left"
              vertical-align="center"
            >
              <div class="work-cell-wrap">
                <span class="cell-text-block">
                  <span>{{ props.row.wname }}</span>
                  <span class="unit-inline">{{ props.row.wunt }}</span>
                </span>
                <span v-if="props.row.work_rowspan > 1" class="multi-material-badge">
                  {{ props.row.work_rowspan }} мат.
                </span>
              </div>
            </custom-table-cell>

            <!-- СМР: Кол-во -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              {{ props.row.wqty }}
            </custom-table-cell>

            <!-- СМР: Цена (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top"><Amount :value="props.row.wprc" :round="2" /></div>
                <div class="split-data-bottom"><Amount :value="props.row.wprc_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- СМР: Сумма (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top">
                  <Amount :value="props.row.wsum" :round="2" />
                  <span v-if="props.row.nds_percent_work_set != null" class="nds-inline">
                    {{ Number(props.row.nds_percent_work_set) }}%
                  </span>
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.wsum_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Материал: Наименование -->
            <custom-table-cell align="left" vertical-align="center" class="material-name-cell">
              <template v-if="props.row.material_id">
                <span class="cell-text-block">
                  <span>{{ props.row.mname }}</span>
                  <span class="unit-inline">{{ props.row.munt }}</span>
                </span>
              </template>
              <span v-else class="tw-text-gray-300 tw-text-xs">—</span>
            </custom-table-cell>

            <!-- Материал: Кол-во -->
            <custom-table-cell align="right" vertical-align="center">
              <span v-if="props.row.material_id">{{ props.row.mqty }}</span>
            </custom-table-cell>

            <!-- Материал: Норма расхода -->
            <custom-table-cell align="right" vertical-align="center">
              <span
                v-if="props.row.norm_ratio > 0"
                class="norm-cell"
                :title="`Норма: ${props.row.norm_ratio.toFixed(4)} на ед. работы`"
              >
                {{ props.row.norm_ratio.toFixed(3) }}
              </span>
            </custom-table-cell>

            <!-- Материал: Цена (с НДС / без НДС) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="split-data" v-if="props.row.material_id">
                <div class="split-data-top"><Amount :value="props.row.mprc" :round="2" /></div>
                <div class="split-data-bottom"><Amount :value="props.row.mprc_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Материал: Сумма (с НДС / без НДС) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="split-data" v-if="props.row.material_id">
                <div class="split-data-top">
                  <Amount :value="props.row.msum" :round="2" />
                  <span v-if="props.row.nds_percent_material != null" class="nds-inline">
                    {{ Number(props.row.nds_percent_material) }}%
                  </span>
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.msum_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Итого (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top">
                  <Amount :value="props.row.ttl" :round="2" />
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.ttl_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
      <q-inner-loading :showing="isLoading" label="Загрузка..." />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Amount from '@/components/common/Amount.vue';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import { getProjectSmetaHistory, getProjectSmetaNew } from './services';
import { downloadFile } from '@/services';

const props = defineProps({
  projectId: { type: Number, required: true },
  projectEntranceId: { type: [Number, String, null], default: null },
});

const isLoading = ref(false);
const isExporting = ref(false);
const data = ref([]);
const meta = ref(null);
const selectedFloor = ref(null);
const currentTotal = ref(null);

const floorOptions = computed(() => {
  const set = new Set();
  data.value.forEach((r) => {
    if (r.level_num !== undefined && r.level_num !== null) set.add(Number(r.level_num));
  });
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((v) => ({ label: `Этаж ${v}`, value: v }));
});

const filteredData = computed(() => {
  if (selectedFloor.value === null || selectedFloor.value === undefined || selectedFloor.value === '') {
    return data.value;
  }
  const target = Number(selectedFloor.value);
  const result = [];
  let pendingHeader = null;
  let activeGroupKey = null;

  for (const r of data.value) {
    if (r.isGroupHeader) {
      if (Number(r.level_num) === target) {
        pendingHeader = r;
        activeGroupKey = null;
      } else {
        pendingHeader = null;
      }
      continue;
    }
    if (Number(r.level_num) !== target) continue;
    if (pendingHeader) {
      result.push(pendingHeader);
      pendingHeader = null;
    }
    if (r.groupKey !== activeGroupKey) {
      activeGroupKey = r.groupKey;
    }
    result.push(r);
  }
  return result;
});

const amount = computed(() => filteredData.value.reduce((acc, item) => acc + (item.ttl || 0), 0));
const amountWithoutNds = computed(() => filteredData.value.reduce((acc, item) => acc + (item.ttl_no_nds || 0), 0));

const isSmetaOutdated = computed(() => {
  if (currentTotal.value === null || data.value.length === 0) return false;
  const historyTotal = data.value.reduce((acc, r) => acc + (r.ttl || 0), 0);
  return Math.abs(historyTotal - currentTotal.value) > 0.01;
});

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const loadData = async () => {
  if (!props.projectId) {
    data.value = [];
    meta.value = null;
    signedAgreementId.value = null;
    currentTotal.value = null;
    return;
  }
  isLoading.value = true;
  const [res, currentRows] = await Promise.all([
    getProjectSmetaHistory(props.projectId, props.projectEntranceId, 'contractor'),
    getProjectSmetaNew(props.projectId, props.projectEntranceId, 'contractor', null),
  ]);
  const rows = res?.rows || [];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].isGroupHeader) continue;
    const next = rows[i + 1];
    rows[i].isLastInWork = !next || next.isGroupHeader || next.groupKey !== rows[i].groupKey;
  }
  data.value = rows;
  meta.value = res?.meta || null;
  selectedFloor.value = null;
  currentTotal.value = (currentRows || []).reduce((acc, r) => acc + (r.ttl || 0), 0);
  isLoading.value = false;
};

const onExport = async () => {
  if (!filteredData.value.length) return;
  isExporting.value = true;
  try {
    const params = {
      project_id: props.projectId,
      what: 'contractor',
    };
    if (props.projectEntranceId) params.project_entrance_id = props.projectEntranceId;
    if (selectedFloor.value !== null && selectedFloor.value !== undefined && selectedFloor.value !== '') {
      params.level_num = selectedFloor.value;
    }

    const verPart = meta.value?.version_no != null ? ` v${meta.value.version_no}` : '';
    const entrPart = props.projectEntranceId ? ` подъезд ${props.projectEntranceId}` : '';
    const title = `Смета договора${entrPart}${verPart}`;

    await downloadFile(
      '/project_list/project/smeta_history_report/excel/',
      title,
      { params },
      'xlsx',
      'get',
      true
    );
  } catch (e) {
    console.error('Export to Excel failed:', e);
  } finally {
    isExporting.value = false;
  }
};

watch(
  () => [props.projectId, props.projectEntranceId],
  () => { loadData(); },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.package-table {
  margin-top: 0;
}

.smeta-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px;
  background: #e3f2fd;
  border-left: 3px solid #1976d2;
  border-radius: 4px;
  font-size: 13px;
  color: #1a1a2e;
  line-height: 1.5;
}

.smeta-outdated-banner {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px;
  background: #fff3e0;
  border-left: 3px solid #ef6c00;
  border-radius: 4px;
  font-size: 13px;
  color: #3e2000;
  line-height: 1.5;
  font-weight: 500;
}

.smeta-action-bar {
  padding: 8px 0;
}

.smeta-history-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.group-header-row {
  .group-header-cell {
    background-color: #e8eaf6;
    font-weight: 700;
    font-size: 13px;
    padding: 6px 12px !important;
    color: #3949ab;
    border-top: 2px solid #c5cae9;
  }
}

.work-cell-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cell-text-block {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.unit-inline {
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  line-height: 1.2;
  margin-top: 1px;
}

.entrance-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.3;

  .entrance-label {
    font-weight: 600;
    font-size: 12px;
  }
  .floor-label {
    font-size: 11px;
    color: #666;
  }
}

.norm-cell {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.nds-inline {
  display: block;
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  font-weight: normal;
  line-height: 1.1;
  margin-top: 1px;
}

.multi-material-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #eef2ff;
  color: #4f46e5;
  font-size: 10px;
  font-weight: 600;
}

.split-data {
  display: flex;
  flex-direction: column;

  .split-data-top {
    padding: 2px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .split-data-bottom {
    padding: 2px 0;
    color: #555;
  }
}

:deep(.th-main) {
  font-weight: 700 !important;
  font-size: 12px;
}

:deep(.th-smr) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f8f9fa !important;
}

:deep(.th-mat) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f0f4ff !important;
}

.th-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7986cb;
  line-height: 1;
  margin-bottom: 2px;
}

.split-header {
  padding: 0 !important;

  .split-header-top {
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .split-header-bottom {
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
}

:deep(.material-name-cell) {
  border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
